import { useContext } from 'react'
import PropTypes from 'prop-types'

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faTrashAlt } from '@fortawesome/pro-light-svg-icons'

import {
  Button, Form, ModalContext, SidebarModal,
} from '@domain-group/suit-theme'

import { digObject } from '@domain-group/javascript-utils'

import { confirmDelete } from '@functions/entity'

import useCurrentUser from '@hooks/useCurrentUser'
import useProviderProductCode, { useProviderProductCodeForm } from '@hooks/useProviderProductCode'

import EntitySelector from '@components/EntitySelector'

const createOrUpdateProviderProductCode = (entityState, props, createFn, updateFn) => {
  const {
    callbacks: { closeModal, createProviderProductCode, updateProviderProductCode },
  } = props

  const text = entityState.id ? 'Updated' : 'Created'
  const actionFn = entityState.id ? updateFn : createFn

  const providerProductCodePayload = {
    callbacks: {
      action: actionFn,
      afterAction: closeModal,
    },
    entityParams: entityState,
    toastText: `Product ${text}`,
  }

  if (entityState.id) return updateProviderProductCode(providerProductCodePayload)

  return createProviderProductCode(providerProductCodePayload)
}

const CreateOrEditProviderProductCodeModal = (props) => {
  const { callbacks, modalKey, showModal } = props

  const { closeModal } = callbacks

  const modalContext = useContext(ModalContext)
  const { modalData } = modalContext

  const modalPayload = digObject(modalData, modalKey, {})
  const { providerProductCode } = modalPayload

  const {
    callbacks: {
      createProviderProductCode: createFn,
      deleteProviderProductCode: deleteFn,
      updateProviderProductCode: updateFn,
    },
    creating,
    deleting,
    provider,
    updating,
  } = useProviderProductCode(providerProductCode)

  const {
    entityState,
    entityState: {
      deep_link_id,
      id,
      product_code,
      service_product_id,
    },
    errors,
    handlers,
    saveEnabled,
    setEntityState,
  } = useProviderProductCodeForm(providerProductCode)

  const confirmDeletePayload = {
    callbacks: {
      afterAction: closeModal,
      deleteFn,
    },
    strings: {
      confirmString: 'Are you sure?',
      entityName: 'Product Code',
    },
  }

  const { isAdmin } = useCurrentUser()

  const saveOrCreateButtonText = id ? 'Save Product' : 'Create Product'

  return (
    <SidebarModal callbacks={callbacks} clickSafeZone modalKey={modalKey} showModal={showModal} size="small">
      <SidebarModal.Header callbacks={callbacks} title={id ? 'Edit' : 'Add'} titleSecondLine="Product Code" />

      <SidebarModal.Content>
        <Form>
          <EntitySelector
            callbacks={{
              selectItem: product => setEntityState({ service_product_id: product ? product.id : null }),
            }}
            disabled={!!id && !isAdmin}
            endpoint="service_products"
            entityKey="serviceProducts"
            errorMessage={errors.service_product_id}
            label="* Product"
            searchRequestOptions={{
              include_service_product_service: true,
            }}
            secondaryTextKey="service.title"
            selectedItemId={service_product_id}
            showEntityId
          />

          <Form.Field label="Product Code" marginTop="large">
            <input name="product_code" value={product_code} type="text" {...handlers} />
          </Form.Field>

          <Form.Field label="Deep Link ID" marginTop="large">
            <select name="deep_link_id" value={deep_link_id} {...handlers}>
              <option value="">Please Select...</option>
              {provider?.deep_links?.map(deepLink => (
                <option
                  key={deepLink.id}
                  value={deepLink.id}
                >
                  {deepLink.title} - #{deepLink.id}
                </option>
              ))}
            </select>
          </Form.Field>

          {id && (
            <Form.Field label="More Options" marginTop="large">
              <Button
                buttonStyle="secondaryUtility"
                icon={<FontAwesomeIcon icon={faTrashAlt} />}
                loading={deleting}
                loadingBubbleColor="white"
                onClick={() => confirmDelete(confirmDeletePayload)}
                size="medium"
              >
                Delete Product Code
              </Button>
            </Form.Field>
          )}
        </Form>
      </SidebarModal.Content>

      <SidebarModal.Footer>
        <Button
          buttonStyle="primaryCreate"
          disabled={!saveEnabled}
          loading={creating || updating}
          onClick={() => createOrUpdateProviderProductCode(entityState, props, createFn, updateFn)}
          size="large"
        >
          {saveEnabled ? saveOrCreateButtonText : 'Complete Fields'}
        </Button>
      </SidebarModal.Footer>
    </SidebarModal>
  )
}

CreateOrEditProviderProductCodeModal.propTypes = {
  callbacks: PropTypes.object.isRequired,
  modalKey: PropTypes.string,
  showModal: PropTypes.bool,
}

CreateOrEditProviderProductCodeModal.defaultProps = {
  modalKey: 'CreateOrEditProviderProductCodeModal',
}

const LazyLoadedModal = props => (
  <SidebarModal.RenderController {...props}>
    <CreateOrEditProviderProductCodeModal {...props} />
  </SidebarModal.RenderController>
)

export default LazyLoadedModal
